import FrameProvider from './FrameProvider';
import { isEmptySet, isNotNil, isNil } from '../../../util/object';
import { chunk } from '../../../util/array';
import { RasteredImageBitmapInterface } from '../RasteredImageBitmapInterface';
import MediaLoaderFactory from '../../media/loader/MediaLoaderFactory';
import { MEDIA_TYPE, SECOND_IN_MILLISECONDS, DEV } from '../../../constants';
import RetryBackoffPolicy from '../../media/loader/RetryBackoffPolicy';
import MediaLoader from '../../media/loader/MediaLoader';
import { info, warn } from '../../../util/log';
import { captureDebugOnce } from '../../../util/sentry';
import PriorityQueue from '../../../model/priority-queue/PriorityQueue';
// import { deferTask } from '../../../util/window'; ADS-36530

const IMAGE_LOAD_RETRY_BACKOFF_TIME = SECOND_IN_MILLISECONDS;

/**
 * ServerFrameProvider, which is used as a wrapper to communicate directly with the
 * server to get a specific frame.
 */
export default class ServerFrameProvider extends FrameProvider {
  /** @property {Map<string, MediaLoader>} */
  _mediaLoaders = new Map();

  _loadFrameRequests = new PriorityQueue();

  _shouldLoadNextFrame = true;

  _currentlyLoadingFrame = false;

  addMediaURL(mediaIdentifier, mediaURL) {
    const mediaURLs = this._mediaByMediaIdentifier.get(mediaIdentifier);
    if (isNil(mediaURLs)) return;

    mediaURLs.add(mediaURL);
  }

  cancel(mediaIdentifier) {
    const mediaURLs = this._mediaByMediaIdentifier.get(mediaIdentifier);
    if (isEmptySet(mediaURLs)) return;

    // cancel media loaders
    mediaURLs.forEach((mediaURL) => {
      const mediaLoader = this._mediaLoaders.get(mediaURL);
      if (isNotNil(mediaLoader)) {
        mediaLoader.cancel();
      }
    });
    this._mediaByMediaIdentifier.delete(mediaIdentifier);
  }

  cleanup = () => {
    this._shouldLoadNextFrame = false;
    this._mediaLoaders.forEach((url, mediaLoader) => {
      if (isNotNil(mediaLoader)) {
        mediaLoader.cancel();
      }
    });
    this._mediaLoaders.clear();
    this._loadFrameRequests.clear();
  };

  /**
   * @function fetchFrames
   * @param  {FrameProviderPrioritizationInterface} frameProviderPrioritizationInterface
   */
  fetchFrames = async (frameProviderPrioritizationInterface) => {
    // Get media identifier (a.k.a creativelayer's id)
    const {
      unavailableFrameRanges,
      videoFile,
    } = frameProviderPrioritizationInterface;

    if (isNil(videoFile)) {
      warn('Could not fetch frames, invalid arguments provided');
      return;
    }

    const { framesPerChunk, mediaIdentifier } = videoFile;

    this.initialize(mediaIdentifier);
    const requestedHeight = this.resolution.getScaledHeight(
      videoFile.width,
      videoFile.height,
    );

    const frameInfo = [];
    unavailableFrameRanges.forEach((unavailableFrameRange) => {
      for (
        let frameNumber = unavailableFrameRange.start;
        frameNumber <= unavailableFrameRange.end;
        frameNumber++
      ) {
        const imageURL = videoFile.getURLByFrameNumber(frameNumber, {
          height: requestedHeight,
        });

        frameInfo.push({
          imageURL,
          frameNumber,
        });
      }
    });

    const groupedFrames = chunk(frameInfo, framesPerChunk);
    for (let i = 0; i < groupedFrames.length; i++) {
      if (!this.shouldContinueExtraction(mediaIdentifier)) {
        info('Canceling extraction!');
        break;
      }

      try {
        await Promise.all(
          groupedFrames[i].map(({ frameNumber, imageURL }) =>
            this.loadFrame(
              mediaIdentifier,
              imageURL,
              frameNumber,
              requestedHeight,
            ),
          ),
        );
      } catch (e) {
        warn('Error with fetching server frames.', e);
      }
    }

    this.extractionCompleted(mediaIdentifier);
  };

  generatePriorityQueueKey = (frameNumber, height) => {
    return `${frameNumber}-${height}`;
  };

  loadFrame = async (
    mediaIdentifier,
    imageURL,
    frameNumber,
    requestedHeight,
    maxTries = MediaLoader.INFINITE_LOAD_ATTEMPTS,
  ) => {
    const mediaLoader = MediaLoaderFactory.getMediaLoaderFromURL(
      imageURL,
      MEDIA_TYPE.IMAGE,
      RetryBackoffPolicy.policyLinearBackoff(IMAGE_LOAD_RETRY_BACKOFF_TIME),
    );
    mediaLoader.maxTries = maxTries;
    this._mediaLoaders.set(imageURL, mediaLoader);
    this.addMediaURL(mediaIdentifier, imageURL);

    try {
      const { image: bitmap } = await mediaLoader.loadMedia();

      if (!(bitmap instanceof ImageBitmap)) {
        // eslint-disable-next-line max-len
        const errMsg = `Expected bitmap of type ${ImageBitmap.name}, could not fetch server frame ${frameNumber} for media: ${mediaIdentifier}`;
        warn(errMsg);
        captureDebugOnce(errMsg);
        this.loadNextFrame();
        return null;
      }

      if (bitmap.height !== requestedHeight) {
        // This is expected locally as we might be getting images from a public bucket and not through AE API which resizes images for us
        if (DEV) {
          const options = {
            resizeWidth: bitmap.width * (requestedHeight / bitmap.height),
            resizeHeight: requestedHeight,
          };
          createImageBitmap(bitmap, options).then((resizedBitmap) => {
            const rasteredImageBitmapInterface = new RasteredImageBitmapInterface(
              mediaIdentifier,
              frameNumber,
              resizedBitmap,
            );
            this.notifyFrameReadyListeners(rasteredImageBitmapInterface);
          });
          this.loadNextFrame();
          return null;
        }
        const errMsg = 'Could not generate media with provided dimensions.';
        const extra = {
          bitmapHeight: bitmap.height,
          requestedHeight,
        };
        warn(errMsg);
        captureDebugOnce(errMsg, extra);
        this.loadNextFrame();
        return null;
      }

      this._mediaLoaders.delete(imageURL);
      const rasteredImageBitmapInterface = new RasteredImageBitmapInterface(
        mediaIdentifier,
        frameNumber,
        bitmap,
      );
      this.notifyFrameReadyListeners(rasteredImageBitmapInterface);

      return bitmap;
    } catch (error) {
      warn('Error fetching frame', error);
      return null;
    }
  };

  loadNextFrame = () => {
    // ADS-36530
    // this._currentlyLoadingFrame = false;
    // if (!this._shouldLoadNextFrame) return;
    // const highestPriorityLoaderInfo = this._loadFrameRequests.dequeue();
    // if (isNil(highestPriorityLoaderInfo)) return;
    // const { value: loadFrameFn } = highestPriorityLoaderInfo;
    // this._currentlyLoadingFrame = true;
    // deferTask(() => loadFrameFn());
  };
}
